// src/core/utils/dateUtils.ts
import moment from 'jalali-moment';

export const getPersianDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  return moment(date).locale('fa').format('jD jMMMM jYYYY');
};

export const getPersianDateOnly = (date: string | Date): string => {
  if (!date) return '';
  return moment(date).locale('fa').format('jYYYY/jMM/jDD');
};

export const getMiladiDateOnly = (date: string): string => {
  if (!date) return '';
  return moment(date, 'jYYYY/jMM/jDD').format('YYYY-MM-DD');
};

export const formatDateTime = (date: string | Date): string => {
  if (!date) return '';
  return moment(date).locale('fa').format('jYYYY/jMM/jDD HH:mm');
};

/** Format RMTO date strings/objects in the Jalali calendar without exposing Gregorian dates in the UI. */
export const formatRmtoPersianDate = (input: unknown): string => {
  let value = Array.isArray(input) ? input[0] : input;
  if (value && typeof value === 'object') {
    const dateParts = value as Record<string, unknown>;
    const year = dateParts.Year ?? dateParts.year;
    const month = dateParts.Month ?? dateParts.month;
    const day = dateParts.Day ?? dateParts.day;
    if (year != null && month != null && day != null) value = `${year}/${month}/${day}`;
  }
  if (typeof value !== 'string' && typeof value !== 'number') return 'تاریخ نامشخص';

  const normalized = String(value)
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .trim();
  let parsed = moment.invalid();

  if (/^(13|14)\d{2}[/-]/.test(normalized)) {
    const jalaliDate = normalized.replace(/-/g, '/').match(/^\d{4}\/\d{1,2}\/\d{1,2}/)?.[0];
    if (jalaliDate) {
      parsed = moment(jalaliDate, 'jYYYY/jM/jD', 'en', true);
      // jalali-moment can roll an out-of-range month/day into the next date.
      const canonicalDate = jalaliDate.split('/').map(Number).join('/');
      if (parsed.isValid() && parsed.format('jYYYY/jM/jD') !== canonicalDate) return 'تاریخ نامشخص';
    }
  } else {
    const isoDate = normalized.match(/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/)?.[0];
    if (isoDate) {
      const [year, month, day] = isoDate.split(/[-/]/);
      parsed = moment(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`, 'YYYY-MM-DD', 'en', true);
    } else {
      const dateFormats = ['M/D/YYYY h:mm:ss A', 'M/D/YYYY', 'D/M/YYYY'];
      for (const format of dateFormats) {
        const candidate = moment(normalized, format, 'en', true);
        if (candidate.isValid()) { parsed = candidate; break; }
      }
    }
  }

  return parsed.isValid() ? parsed.locale('fa').format('jYYYY/jMM/jDD') : 'تاریخ نامشخص';
};

/**
 * Format a waybill issue time for the RMTO API.
 * RMTO requires HH:mm:ss, while the UI and the info endpoint use HH:mm or
 * HH:mm:ss. Normalize Persian/Arabic digits as well because the value may
 * come from a localized input or response.
 */
export const formatRmtoIssueTime = (time: string | null | undefined): string => {
  if (!time) return '';

  const asciiTime = String(time)
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .trim();
  const parts = asciiTime.split(/[.:\s]+/).filter(Boolean);

  if (parts.length < 2 || parts.length > 3 || parts.some((part) => !/^\d+$/.test(part))) {
    return '';
  }

  const [hours, minutes, seconds = 0] = parts.map(Number);
  if (hours > 23 || minutes > 59 || seconds > 59) return '';

  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
};

// Backward-compatible names used by the legacy date utility tests and callers.
export const formatPersianDate = (date: string | Date | null | undefined): string =>
  date ? getPersianDate(date) : '-';
export const formatGregorianDate = (date: string | Date | null | undefined): string =>
  date ? getMiladiDateOnly(typeof date === 'string' ? date : date.toISOString()) : '';
